import { useEffect } from 'react';
import { FilesServiceProvider } from '@carboncopyco/components';
import { Loader } from '@carboncopyco/ui';
import {
  setGlobalContext,
  setUser,
  clearGlobalContext,
  clearUser,
} from '@carboncopyco/logger';

import { settings } from '../../../settings';
import ProtectedAppModals from '../components/Modals/ProtectedAppModals/ProtectedAppModals';
import ProtectedEventListener from '../events/ProtectedEventListener';
import useAsyncMemo from '../hooks/useAsyncMemo';
import InstallUpdatesModal from '../modals/InstallUpdatesModal/InstallUpdatesModal';
import { SessionIdleModal } from '../modals/SessionIdleModal';
import LoginScreen from '../routes/LoginPage/LoginScreen';
import MainScreen from '../screens/MainScreen';
import SpeakerPanelScreen from '../screens/SpeakerPanelScreen';
import { LogrocketWindowType, initLogRocket } from '../shared/utils/tracking';
import { useUser } from '../store/context';
import {
  LDProviderWrapper,
  PermissionProviderWrapper,
} from '../store/protected';
import TourView from '../views/TourView';

export const MainApp = () => {
  const { setMe, setPersonas } = useUser();
  const { data: user, isLoading } = useAsyncMemo<Record<any, any>>(async () => {
    const data = await window.dsDesktop?.me();
    if (data) setMe(data);
    const personas = await window.dsDesktop?.user.getPersonas();
    setPersonas(personas);
    return data;
  }, []);

  const isAuthenticated = !!user?.email;
  const isSpeakerPanelScreen = window?.location?.href?.includes('present');
  const updateApp = window?.location?.href?.includes('updateApp');
  const isTour = window?.location?.href?.includes('tour');

  useEffect(() => {
    setUser({ email: user?.email, org: user?.default_organization });

    window.dsDesktop.app.getSystemInfo().then((info) => {
      const { memory, ...systemInfo } = info;
      setGlobalContext({
        type: 'live',
        organization_id: user?.default_organization,
        scopeName: 'console',
        memory,
        systemInfo,
      });
    });

    return () => {
      clearUser();
      clearGlobalContext();
    };
  }, [user?.default_organization, user?.email]);

  if (isLoading) return <Loader show />;

  if (updateApp) {
    return <InstallUpdatesModal />;
  }
  if (!isAuthenticated) {
    initLogRocket(LogrocketWindowType.LOGIN_SCREEN);
    return <LoginScreen />;
  }
  if (isTour) {
    return (
      <ProtectedEventListener>
        <FilesServiceProvider
          filesServiceDomain={settings.FILES_SERVICE_URL ?? ''}
        >
          <LDProviderWrapper user={user}>
            <TourView />
          </LDProviderWrapper>
        </FilesServiceProvider>
      </ProtectedEventListener>
    );
  }
  if (isSpeakerPanelScreen) {
    initLogRocket(LogrocketWindowType.SPEAKER_PANEL, user);
    return (
      <LDProviderWrapper user={user}>
        <SpeakerPanelScreen />
      </LDProviderWrapper>
    );
  }

  initLogRocket(LogrocketWindowType.DESKTOP_APP, user);

  return (
    <PermissionProviderWrapper>
      <ProtectedEventListener>
        <FilesServiceProvider
          filesServiceDomain={settings.FILES_SERVICE_URL ?? ''}
        >
          <LDProviderWrapper user={user}>
            <MainScreen />
            <ProtectedAppModals />
            <SessionIdleModal />
          </LDProviderWrapper>
        </FilesServiceProvider>
      </ProtectedEventListener>
    </PermissionProviderWrapper>
  );
};
